import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Check, X, Settings2, Pencil, Trash2 } from "lucide-react";

const ADD = "__add__";

export default function DirectoryPicker({ endpoint, placeholder, groupBy, getLabel, createFields, onPick, testid }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [manageOpen, setManageOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const pick = (id) => {
    const it = items.find((x) => x.id === id);
    if (it) { setSelected(id); onPick(it); }
  };

  const renderFields = (f, val, onChange) => (
    f.type === "select" ? (
      <Select value={val || ""} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Odaberi..." /></SelectTrigger>
        <SelectContent className="bg-popover">
          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    ) : (
      <Input value={val || ""} onChange={(e) => onChange(e.target.value)} />
    )
  );

  const save = async () => {
    for (const f of createFields) {
      if (f.required && !(form[f.key] || "").trim()) { toast.error(`Polje "${f.label}" je obavezno`); return; }
    }
    try {
      const res = await api.post(`/${endpoint}`, form);
      await load();
      setSelected(res.data.id);
      onPick(res.data);
      setAdding(false);
      setForm({});
      toast.success("Spremljeno u imenik");
    } catch (e) {
      toast.error("Greška pri spremanju");
    }
  };

  const saveEdit = async () => {
    for (const f of createFields) {
      if (f.required && !(editForm[f.key] || "").trim()) { toast.error(`Polje "${f.label}" je obavezno`); return; }
    }
    try {
      await api.put(`/${endpoint}/${editId}`, editForm);
      await load();
      setEditId(null);
      setEditForm({});
      toast.success("Izmjena spremljena");
    } catch (e) {
      toast.error("Greška pri spremanju izmjene");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/${endpoint}/${id}`);
      await load();
      if (selected === id) setSelected("");
      toast.success("Obrisano iz imenika");
    } catch (e) {
      toast.error("Greška pri brisanju");
    }
  };

  const startEdit = (it) => {
    const base = {};
    createFields.forEach((f) => (base[f.key] = it[f.key] ?? ""));
    setEditId(it.id);
    setEditForm(base);
  };

  const groups = {};
  if (groupBy) {
    items.forEach((it) => {
      const g = it[groupBy] || "Ostalo";
      (groups[g] = groups[g] || []).push(it);
    });
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Select value={selected} onValueChange={(v) => (v === ADD ? setAdding(true) : pick(v))}>
          <SelectTrigger data-testid={testid} className="flex-1"><SelectValue placeholder={placeholder || "Odaberi iz imenika..."} /></SelectTrigger>
          <SelectContent className="bg-popover max-h-72">
            {groupBy
              ? Object.keys(groups).map((g) => (
                  <SelectGroup key={g}>
                    <SelectLabel className="text-xs font-bold text-primary">{g}</SelectLabel>
                    {groups[g].map((it) => <SelectItem key={it.id} value={it.id}>{getLabel(it)}</SelectItem>)}
                  </SelectGroup>
                ))
              : items.map((it) => <SelectItem key={it.id} value={it.id}>{getLabel(it)}</SelectItem>)}
            <SelectItem value={ADD} className="text-blue-600 font-semibold">
              <span className="flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Novi unos u imenik...</span>
            </SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" size="icon" variant="outline" onClick={() => setManageOpen(true)} data-testid={`${testid}-manage`} title="Uredi imenik">
          <Settings2 className="h-4 w-4" />
        </Button>
      </div>

      {adding && (
        <div className="rounded-lg border bg-secondary/40 p-3 space-y-2" data-testid={`${testid}-add-panel`}>
          {createFields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs font-semibold text-muted-foreground">{f.label}</Label>
              {renderFields(f, form[f.key], (v) => setForm({ ...form, [f.key]: v }))}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button type="button" size="sm" onClick={save} data-testid={`${testid}-new-save`}><Check className="h-4 w-4 mr-1" /> Spremi</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setAdding(false); setForm({}); }}><X className="h-4 w-4 mr-1" /> Odustani</Button>
          </div>
        </div>
      )}

      <Dialog open={manageOpen} onOpenChange={(o) => { setManageOpen(o); if (!o) { setEditId(null); setEditForm({}); } }}>
        <DialogContent className="bg-card sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid={`${testid}-manage-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">Uredi imenik</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            {items.length === 0 && <p className="text-sm text-muted-foreground">Imenik je prazan.</p>}
            {items.map((it) => (
              editId === it.id ? (
                <div key={it.id} className="rounded-lg border bg-secondary/40 p-3 space-y-2" data-testid={`${testid}-edit-panel`}>
                  {createFields.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs font-semibold text-muted-foreground">{f.label}</Label>
                      {renderFields(f, editForm[f.key], (v) => setEditForm({ ...editForm, [f.key]: v }))}
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <Button type="button" size="sm" onClick={saveEdit} data-testid={`${testid}-edit-save`}><Check className="h-4 w-4 mr-1" /> Spremi</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { setEditId(null); setEditForm({}); }}><X className="h-4 w-4 mr-1" /> Odustani</Button>
                  </div>
                </div>
              ) : (
                <div key={it.id} className="flex items-center gap-2 rounded-md border px-2 py-1.5">
                  <span className="flex-1 text-sm truncate">{getLabel(it)}</span>
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(it)} data-testid={`${testid}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(it.id)} data-testid={`${testid}-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
